<template>
	<div
		v-if="items?.length"
		class="rewards-grid"
		:class="`rewards-grid--count-${count} rewards-grid--${context}`"
	>
		<div v-for="(item, idx) in items" :key="idx" class="rewards-grid__item">
			<Tooltip
				v-if="item"
				:heading="
					isMultiplier(item.key)
						? multiplierHeading(
								translate(`resource.${item.key}`),
								item.qty,
							)
						: translate(`resource.${item.key}`)
				"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<div class="rewards-grid__value">
						<div
							class="rewards-grid__icon"
							:class="`offer-${contentKind}-icon--${item.key.toLowerCase()}-${dynamicSize.icon}`"
						/>
					</div>
					<div class="rewards-grid__label">
						<div
							v-if="isMultiplier(item.key)"
							class="computed-value"
							:style="{
								fontSize: `${dynamicSize.price / 16}rem`,
							}"
						>
							<span>
								{{
									`${formatNumber(multiplierMinValue(item.qty))} min`
								}}
							</span>
						</div>
						<AppPrice
							v-else
							:amount="item.qty"
							:currency="item.key"
							:fontSize="dynamicSize.price"
							:iconSize="dynamicSize.priceIcon"
						/>
					</div>
					<div
						v-if="context === RewardsGridContext.POPUP"
						class="reward-item-label-wrapper"
					>
						<ShrinkText
							:text="
								isMultiplier(item.key)
									? multiplierHeading(
											translate(`resource.${item.key}`),
											item.qty,
										)
									: translate(`resource.${item.key}`)
							"
							:fontSize="count > 2 ? 12 : 16"
							containerClassList="reward-item-label"
							isBold
						/>
					</div>
				</template>
				<template #tooltip>
					<span class="reward-tooltip">
						{{
							replacePlaceholder(
								translate(`resource.${item.key}_description`),
								"{number}",
								getReplaceValue(item.key) ?? 0,
							)
						}}
					</span>
				</template>
			</Tooltip>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { ResourceOrUtilitySlotItem } from "@/types/shop";
import { CommodityGroup } from "@/types/shop";
import { Placements, RewardsGridContext } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import {
	getReplaceValue,
	isMultiplier,
	multiplierHeading,
	multiplierMinValue,
} from "@/utils/shop/values";
import { translate } from "@/utils/translate";
import { computed } from "vue";

interface Props {
	items: Array<ResourceOrUtilitySlotItem> | null;
	context: RewardsGridContext;
}

const { items, context } = defineProps<Props>();

const count = computed((): number => items?.length ?? 0);

const contentKind = computed(
	(): CommodityGroup.RESOURCE | CommodityGroup.UTILITY | null => {
		if (!items) return null;
		const first = items[0];
		if (!first) return null;
		if (first.group === CommodityGroup.RESOURCE)
			return CommodityGroup.RESOURCE;
		if (first.group === CommodityGroup.UTILITY)
			return CommodityGroup.UTILITY;
		return null;
	},
);

const dynamicSize = computed((): Record<string, number> => {
	if (context === RewardsGridContext.POPUP) {
		switch (count.value) {
			case 1:
				return { icon: 304, price: 30, priceIcon: 20 };
			case 2:
				return { icon: 184, price: 26, priceIcon: 20 };
			case 3:
				return { icon: 152, price: 18, priceIcon: 20 };
			case 4:
				return { icon: 152, price: 18, priceIcon: 20 };
			case 5:
				return { icon: 144, price: 18, priceIcon: 20 };
			case 6:
				return { icon: 144, price: 18, priceIcon: 20 };
			default:
				return { icon: 80, price: 20, priceIcon: 20 };
		}
	} else {
		switch (count.value) {
			case 1:
				return { icon: 144, price: 14, priceIcon: 16 };
			case 2:
				return { icon: 120, price: 14, priceIcon: 16 };
			case 3:
				return { icon: 72, price: 8, priceIcon: 8 };
			case 4:
				return { icon: 72, price: 8, priceIcon: 8 };
			case 5:
				return { icon: 72, price: 8, priceIcon: 8 };
			case 6:
				return { icon: 72, price: 8, priceIcon: 8 };
			default:
				return { icon: 80, price: 20, priceIcon: 10 };
		}
	}
});
</script>

<style lang="scss" scoped>
.rewards-grid {
	display: grid;
	position: relative;
	grid-template-columns: repeat(3, 1fr);
	align-items: center;
	justify-items: center;
	z-index: 1;
	padding: 0.625rem;

	&--card {
		.rewards-grid__value {
			&::before {
				display: none; // for radial-glow
			}
		}
	}

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__item {
		@include flex-col-center;
		row-gap: 6px;
		justify-items: center;
		width: 100%;

		.v-popper {
			width: 100%;
		}

		:deep(.computed-value) {
			@include flex-row-center;
			gap: 0.25rem;
			z-index: 1;
			margin: 0 0.2rem;
			color: #fff3dd;
			font-weight: 800;
			line-height: 1.1;

			span {
				padding-bottom: 0.0625rem;
			}
		}
	}

	&__value {
		position: relative;
		width: 100%;
		height: 7rem;

		.rewards-grid__icon {
			position: absolute; // to prevent the sprite from overflowing the grid with transparent areas
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
		}
	}

	&__label {
		@include flex-row-center;
		position: relative;
		mask-image: linear-gradient(
			to right,
			transparent 2%,
			black 20%,
			black 80%,
			transparent 98%
		);
		border-width: 0.063rem 0 0.063rem 0;
		border-style: solid;
		border-image-source: linear-gradient(
			to bottom,
			rgba(216, 169, 114, 1),
			rgba(226, 181, 145, 1)
		);
		border-image-slice: 1;
		width: 100%;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			@include background(
				url($path-textures + "silk-flag-texture-bg.avif"),
				cover,
				center center,
				no-repeat
			);
			z-index: 1;
			mix-blend-mode: multiply;
			width: 100%;
			height: 100%;
			content: "";
		}

		&::after {
			position: absolute;
			top: 0;
			left: 0;
			background: rgb(112, 62, 16);
			width: 100%;
			height: 100%;
			content: "";
		}

		.price {
			z-index: 1;
		}
	}

	/* 1 item: center */
	&--count-1 {
		grid-template-columns: 1fr;
		gap: 0.75rem;
		padding-bottom: 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.5rem;

			.rewards-grid__value {
				min-width: 6.5rem;
				height: 5.5rem;
			}

			.rewards-grid__label {
				height: 1.75rem;

				:deep(.shrink-text) {
					padding-bottom: 0.125rem;
				}
			}
		}

		.rewards-grid__value {
			height: 12rem;

			&::before {
				position: absolute;
				top: 65%;
				left: 50%;
				width: 160%;
				height: 160%;
				content: "";
				@include background(
					url($path-images + "shop/backgrounds/radial-glow-xl.avif"),
					contain,
					center,
					no-repeat
				);
				transform: translate(-50%, -50%);
				z-index: -1;
				animation: rotateInfinite 20s linear infinite;
				pointer-events: none;
			}
		}

		.rewards-grid__label {
			height: 2.125rem;
		}

		.price {
			:deep(.shrink-text) {
				padding-bottom: 0.5rem;
			}
		}
	}

	/* 2 items: 2 columns */
	&--count-2 {
		grid-template-columns: repeat(2, 1fr);
		padding: 2rem 0.625rem 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;

			.rewards-grid__value {
				min-width: 5.5rem;
				height: 4.5rem;
			}

			.rewards-grid__label {
				height: 1.75rem;

				:deep(.shrink-text) {
					padding-bottom: 0.125rem;
				}
			}
		}

		.rewards-grid__value {
			height: 6.5rem;
		}

		.rewards-grid__label {
			height: 2.125rem;
		}

		.price {
			:deep(.shrink-text) {
				padding-bottom: 0.5rem;
			}
		}
	}

	/* 3 items: 1 top center, 2 bottom */
	&--count-3 {
		grid-template-columns: repeat(3, 1fr);
		grid-template-areas:
			". a ."
			"b . c";
		padding: 2rem 0.625rem 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;

			.rewards-grid__value {
				min-width: 3.5rem;
				height: 2.5rem;
			}
		}

		> .rewards-grid__item:nth-child(1) {
			grid-area: a;
		}
		> .rewards-grid__item:nth-child(2) {
			grid-area: b;
		}
		> .rewards-grid__item:nth-child(3) {
			grid-area: c;
		}

		.rewards-grid__value {
			height: 4.25rem;
		}
	}

	/* 4 items: 2×2 */
	&--count-4 {
		grid-template-columns: repeat(2, 1fr);
		grid-template-areas:
			"a b"
			"c d";
		column-gap: 1.75rem;
		row-gap: 1.5rem;
		padding: 2rem 0.625rem 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;

			.rewards-grid__value {
				min-width: 3.5rem;
				height: 2.5rem;
			}
		}

		> .rewards-grid__item:nth-child(1) {
			grid-area: a;
		}
		> .rewards-grid__item:nth-child(2) {
			grid-area: b;
		}
		> .rewards-grid__item:nth-child(3) {
			grid-area: c;
		}
		> .rewards-grid__item:nth-child(4) {
			grid-area: d;
		}

		.rewards-grid__value {
			height: 4.25rem;
		}
	}

	/* 5 items: 2 top, 3 bottom */
	&--count-5 {
		grid-template-columns: repeat(3, 1fr);
		grid-template-areas:
			"a b c"
			"d e .";
		row-gap: 1.5rem;
		padding: 2rem 0.625rem 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;

			.rewards-grid__value {
				min-width: 3.5rem;
				height: 2.5rem;
			}
		}

		> .rewards-grid__item:nth-child(1) {
			grid-area: a;
		}
		> .rewards-grid__item:nth-child(2) {
			grid-area: b;
		}
		> .rewards-grid__item:nth-child(3) {
			grid-area: c;
		}
		> .rewards-grid__item:nth-child(4) {
			grid-area: d;
			margin-right: -100%; // gfx
		}
		> .rewards-grid__item:nth-child(5) {
			grid-area: e;
			margin-right: -100%; // gfx
		}

		.rewards-grid__value {
			height: 4.25rem;
		}
	}

	/* 6 items: 3 top, 3 bottom */
	&--count-6 {
		grid-template-columns: repeat(3, 1fr);
		grid-template-areas:
			"a b c"
			"d e f";
		row-gap: 1.5rem;
		padding: 2rem 0.625rem 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;

			.rewards-grid__value {
				min-width: 3.5rem;
				height: 2.5rem;
			}
		}

		> .rewards-grid__item:nth-child(1) {
			grid-area: a;
		}
		> .rewards-grid__item:nth-child(2) {
			grid-area: b;
		}
		> .rewards-grid__item:nth-child(3) {
			grid-area: c;
		}
		> .rewards-grid__item:nth-child(4) {
			grid-area: d;
		}
		> .rewards-grid__item:nth-child(5) {
			grid-area: e;
		}
		> .rewards-grid__item:nth-child(6) {
			grid-area: f;
		}

		.rewards-grid__value {
			height: 4.25rem;
		}
	}

	.reward-item {
		&-label {
			position: absolute;
			margin-top: 0.5rem;
			width: 100%;
			color: #f2dcb5;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

			// IMPORTANT: wrapper for ShrinkText container
			&-wrapper {
				position: relative;
				width: 100%;
				height: 1.125rem;
				@include flex-row-center;
			}
		}
	}
}
</style>
